import { Injectable, inject } from '@angular/core';

import { ParticipantImage } from '@cds/betting-offer';
import { MasterDataInfoResponse } from '@cds/betting-offer/domain-specific';
import { MasterDataInfoRequest } from '@cds/query-objects';
import { ExpiringCache, ExpiringCacheFactory } from '@frontend/sports/common/core/utils/expiring-cache';
import { NumberDictionary, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { CompetitionDetail } from '@frontend/sports/types/components/team-pages';
import { Observable, map } from 'rxjs';

import { BaseCdsApi } from './cds-api.service';
import { BaseCdsApiFactory, CDS_API_FACTORY } from './cds-api.service.factory';

@Injectable({ providedIn: 'root' })
export class MasterdataApiService {
    private teamsByComepetitionCache: ExpiringCache;
    private cdsApi: BaseCdsApi;

    constructor() {
        const cdsApiServiceFactory = inject<BaseCdsApiFactory>(CDS_API_FACTORY);
        const cacheFactory = inject(ExpiringCacheFactory);

        this.cdsApi = cdsApiServiceFactory({ endpoint: '/masterdata' });
        this.teamsByComepetitionCache = cacheFactory.create(600);
    }

    getCompetitionsAndTeamsCount(competitions: CompetitionDetail[]): Observable<CompetitionDetail[]> {
        return this.getCompetitionCount(competitions).pipe(
            map((response) => {
                if (!response) {
                    return [];
                }

                return competitions.filter((c) => response[c.competitionId]);
            }),
        );
    }

    getCompetitionCount(competitions: CompetitionDetail[]): Observable<StringDictionary<number> | undefined> {
        const compIds = competitions.map((s) => s.competitionId).join(',');
        const source = this.cdsApi.get<StringDictionary<number>>('/competitions/participants/counts/v2', { competitionIds: compIds });

        return this.teamsByComepetitionCache.getOrCreate(`team-pages-competitions-${compIds}`, source);
    }

    getImagesInfo(sportId: number, participantIds: number[]): Observable<NumberDictionary<ParticipantImage>[] | undefined> {
        const participants = participantIds.map((s) => s).join(',');
        const source = this.cdsApi.get<NumberDictionary<ParticipantImage>[]>('/info/participants', {
            sportId,
            participantIds: participants,
        });

        return this.teamsByComepetitionCache.getOrCreate(`team-pages-participants-${participantIds}`, source);
    }

    getVirtualCompetitionInfo(request: MasterDataInfoRequest): Observable<MasterDataInfoResponse | undefined> {
        return this.cdsApi.get<MasterDataInfoResponse>('/info/virtual-competition', request);
    }
}
